"use client";

import { Eye } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import { TwitchEmbed, useTwitchStream, useTwitchUser } from "@/features/stream";
import { formatNumberUi, toUiLocale } from "@/shared/lib/ui-locale";

// Официальная трансляция турнира (вкладка «Обзор»). Аккуратный ambient-вид:
// полноразмерный плеер 16:9 + компактный боковой сайдбар с описанием.
// Данные — те же хуки, что на вкладке стримов профиля (useTwitchStream/User),
// плеер — TwitchEmbed. Канал (Twitch login) приходит пропом; блок рендерится,
// только когда канал live — офлайн/не идёт стрим = блока нет вообще (см. KAN-92).
// Live-статус переопрашивается раз в ~75с, чтобы блок сам скрывался по окончании стрима.
const STREAM_REFETCH_INTERVAL_MS = 75_000;

const TWITCH_PURPLE = "#9146FF";

const TwitchGlyph = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
  </svg>
);

interface TournamentOfficialStreamProps {
  channel: string;
}

export const TournamentOfficialStream = ({
  channel,
}: TournamentOfficialStreamProps) => {
  const tUi = useTranslations("Ui");
  const locale = toUiLocale(useLocale());
  const { stream } = useTwitchStream(channel, {
    refetchIntervalMs: STREAM_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });
  const { user } = useTwitchUser(channel);

  const isLive = Boolean(stream && stream.type === "live");
  const title = stream?.title?.trim() || user?.display_name || channel;
  const gameName = stream?.game_name?.trim();
  const viewers = stream?.viewer_count ?? 0;

  // Оффлайн/стрим ещё не начался/уже закончился — блока нет вообще (не заглушка).
  if (!isLive) return null;

  return (
    <div
      className="overflow-hidden rounded-[20px] border animate-in fade-in duration-300"
      style={{
        borderColor: "color-mix(in srgb, #9146FF 40%, var(--border))",
        background:
          "linear-gradient(180deg, color-mix(in srgb, #9146FF 10%, var(--card)), var(--card))",
      }}
    >
      <div className="flex flex-col lg:flex-row">
        {/* Плеер — полноразмерный ambient, 16:9 (TwitchEmbed растягивается на родителя) */}
        <div className="relative aspect-video w-full bg-black lg:flex-1">
          <TwitchEmbed channel={channel} muted />
        </div>

        {/* Компактный боковой сайдбар с описанием */}
        <div className="flex w-full shrink-0 flex-col gap-3 p-4 lg:w-64">
          <span
            className="inline-flex items-center gap-1.5 self-start rounded-lg px-2 py-1 text-[10px] font-extrabold uppercase tracking-[0.06em]"
            style={{
              background: "color-mix(in srgb, #9146FF 16%, transparent)",
              color: "#a970ff",
            }}
          >
            <TwitchGlyph className="h-3 w-3" />
            {tUi("tournamentOfficialStream.ofitsialnayaTranslyatsiya")}
          </span>

          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-[#ef4444] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.06em] text-white">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
              {tUi("tournamentOfficialStream.vEfire")}
            </span>
            <span className="inline-flex items-center gap-1 rounded-md bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-400">
              <Eye className="h-3 w-3" />
              {formatNumberUi(viewers, undefined, locale)}
            </span>
          </div>

          <div
            className="line-clamp-2 text-sm font-bold leading-snug text-foreground"
            style={{ fontFamily: "var(--font-heading)" }}
            title={title}
          >
            {title}
          </div>

          {gameName ? (
            <span className="inline-flex w-fit items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {gameName}
            </span>
          ) : null}

          <a
            href={`https://www.twitch.tv/${channel}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto inline-flex items-center justify-center gap-2 rounded-[10px] px-3 py-2 text-sm font-bold text-white transition-colors"
            style={{
              background: TWITCH_PURPLE,
              boxShadow: "0 6px 18px -8px rgba(145,70,255,.8)",
            }}
          >
            <TwitchGlyph className="h-4 w-4" />
            {tUi("tournamentOfficialStream.smotretNaTwitch")}
          </a>

          <div
            className="truncate text-center text-[11px] text-muted-foreground"
            style={{ fontFamily: "var(--font-mono)" }}
          >
            twitch.tv/{channel}
          </div>
        </div>
      </div>
    </div>
  );
};
